import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { TOKEN_KEY } from "../api";
import { ApiResponse, Role, User } from "../types";

interface AuthContextValue {
    user: User | null;
    /** true while the saved token is being checked on page load */
    loading: boolean;
    login: (username: string, password: string) => Promise<User>;
    logout: () => void;
    refreshUser: () => Promise<void>;
    hasRole: (...roles: Role[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/** Where each role lands after login */
export const homeFor = (role: Role | undefined): string =>
    role === "SUPERADMIN" || role === "RESELLER" ? "/dashboard" : "/profile";

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState<boolean>(() => !!localStorage.getItem(TOKEN_KEY));

    const logout = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
    }, []);

    const refreshUser = useCallback(async () => {
        const res = await api.get<ApiResponse<User>>("/users/me");
        setUser(res.data.data);
    }, []);

    // Restore the session from the saved token
    useEffect(() => {
        if (!localStorage.getItem(TOKEN_KEY)) return;

        refreshUser()
            .catch(() => logout())
            .finally(() => setLoading(false));
    }, [refreshUser, logout]);

    // api.ts fires this on any 401 (expired token, deleted user)
    useEffect(() => {
        const onLogout = () => logout();
        window.addEventListener("auth:logout", onLogout);
        return () => window.removeEventListener("auth:logout", onLogout);
    }, [logout]);

    // Logging out in one tab logs out the others
    useEffect(() => {
        const onStorage = (e: StorageEvent) => {
            if (e.key === TOKEN_KEY && !e.newValue) setUser(null);
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, []);

    const login = useCallback(async (username: string, password: string) => {
        const res = await api.post<{ token: string; user: User }>("/auth/login", { username, password });

        localStorage.setItem(TOKEN_KEY, res.data.token);
        setUser(res.data.user);

        // Pull the full profile (phone, linked customer) in the background
        refreshUser().catch(() => undefined);

        return res.data.user;
    }, [refreshUser]);

    const hasRole = useCallback((...roles: Role[]) => !!user && roles.includes(user.role), [user]);

    const value = useMemo(
        () => ({ user, loading, login, logout, refreshUser, hasRole }),
        [user, loading, login, logout, refreshUser, hasRole]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
    return ctx;
}